import React, { useEffect, useMemo, useState } from "react";
import { useAppFolderUrl } from "../hooks/useAppFolderUrl";
import { useAuth } from "../hooks/useAuth";
import { usePhotoQueue } from "../hooks/usePhotoQueue";
import {
  Product,
  ProductStatus,
  dayCountLabel,
  groupProductsByDay,
  productTime,
} from "../utils/product";
import ThumbnailGrid from "./ThumbnailGrid";

interface ProductListProps {
  /** Ouvre le viseur : sur un produit neuf sans argument, sur celui-ci pour le compléter. */
  onStart: (productId?: string) => void;
}

/** Le jour `AAAA-MM-JJ` lu à midi local : aucun fuseau ne le fait glisser. */
const dayLabel = (day: string): string =>
  new Intl.DateTimeFormat("fr-FR", { dateStyle: "full" }).format(
    new Date(`${day}T12:00:00`)
  );

/**
 * L'état d'un produit se lit de trois façons à la fois : le glyphe le porte
 * d'un coup d'œil, la couleur le double, le mot reste pour qui le lit. Le
 * glyphe est décoratif — le mot suffit aux lecteurs d'écran.
 *
 * L'intensité est inverse à la normalité. Depuis que l'appareil garde sept
 * jours d'envois, « envoyé » couvre presque tout l'écran : c'est le cas normal,
 * il est muet. Chaque couleur va avec son `-content`, seul couple dont
 * `tests/contrast.test.ts` répond.
 */
const BADGES: Record<
  ProductStatus,
  { className: string; glyph: string; label: string }
> = {
  failed: { className: "badge-error", glyph: "!", label: "Échec" },
  sending: {
    className: "badge-info animate-pulse",
    glyph: "↑",
    label: "Envoi…",
  },
  pending: { className: "badge-warning", glyph: "●", label: "À envoyer" },
  sent: {
    className: "bg-base-300 border-base-300 text-base-content",
    glyph: "✓",
    label: "Envoyé",
  },
};

/**
 * Un produit tient sur une ligne pleine largeur : il y a la place d'écrire son
 * état en toutes lettres, ce que n'avait pas la vignette d'une grille.
 *
 * L'échec prend un liseré, jamais un lavis : un fond teinté sous du texte
 * atténué tombe sous le seuil AA. Les lignes conformes portent le même liseré
 * en transparent, pour que rien ne se décale.
 */
const ProductRow: React.FC<{ product: Product; onOpen: () => void }> = ({
  product,
  onOpen,
}) => {
  const cover = product.photos[0];
  const badge = BADGES[product.status];
  const imageUrl = useMemo(
    () => URL.createObjectURL(cover.thumbnailBlob),
    [cover.thumbnailBlob]
  );

  useEffect(() => () => URL.revokeObjectURL(imageUrl), [imageUrl]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full min-h-14 flex items-center gap-3 px-4 py-2 text-left border-l-4 ${
        product.status === "failed" ? "border-l-error" : "border-l-transparent"
      }`}
      aria-label={`Ouvrir les photos du produit de ${productTime(product.id)}`}
    >
      <img
        src={imageUrl}
        alt=""
        className="w-12 h-12 rounded-lg object-cover bg-base-200 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="font-bold tabular-nums">{productTime(product.id)}</div>
        <div className="text-sm text-base-content/70">
          {product.photos.length} photo(s)
        </div>
      </div>
      <span className={`badge gap-1 shrink-0 ${badge.className}`}>
        <span aria-hidden="true">{badge.glyph}</span>
        {badge.label}
      </span>
    </button>
  );
};

/**
 * L'accueil : les produits de l'opérateur, en lignes groupées par jour.
 *
 * Il montre la fenêtre glissante de sept jours, envois compris — une photo
 * arrivée à destination reste sur l'appareil jusqu'à la purge, et la pastille
 * de son produit le dit. Au-delà de la fenêtre, c'est le stockage durable qui
 * tient l'historique : d'où le lien, à l'écran sans qu'on ait à défiler.
 */
const ProductList: React.FC<ProductListProps> = ({ onStart }) => {
  const { user } = useAuth();
  const { photos, loading, removePhoto } = usePhotoQueue(user);
  const { data: folderUrl, isLoading: folderLoading } = useAppFolderUrl(user);
  const days = groupProductsByDay(photos);
  const [openId, setOpenId] = useState<string | null>(null);
  // Le produit ouvert se relit dans les produits du moment : vidé de ses photos
  // — supprimées une à une, ou purgées — il cesse d'exister et la liste
  // reprend la main.
  const openProduct = days
    .flatMap((day) => day.products)
    .find((product) => product.id === openId);

  if (openProduct) {
    return (
      <div className="flex flex-col h-full bg-base-200">
        <div className="flex items-center gap-2 p-2 bg-base-100 border-b border-base-300">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setOpenId(null)}
          >
            ← Produits
          </button>
          <span className="text-sm text-base-content/70 first-letter:uppercase">
            {dayLabel(openProduct.day)} · {openProduct.photos.length} photo(s)
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <ThumbnailGrid photos={openProduct.photos} onDelete={removePhoto} />
        </div>

        <div className="p-4 border-t border-base-300 bg-base-100">
          {/* Le même produit se rouvre : ses nouvelles photos rejoignent son
              dossier distant, que celui-ci existe déjà ou non. */}
          <button
            className="btn btn-primary btn-block btn-lg"
            onClick={() => onStart(openProduct.id)}
          >
            Ajouter des photos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-base-100">
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <span className="loading loading-spinner loading-lg text-primary"></span>
          </div>
        ) : days.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-base-content/70 gap-2 p-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p>Démarrez pour photographier l'étiquette d'un produit.</p>
            <p className="text-xs">
              Les produits des sept derniers jours s'afficheront ici.
            </p>
          </div>
        ) : (
          <>
            {days.map(({ day, products }) => (
              <section key={day}>
                {/* L'en-tête reste à l'écran pendant qu'on parcourt sa section,
                    et compte : « à envoyer / total » dit la journée sans
                    qu'on ait à la lire ligne à ligne. */}
                <h2 className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-2 bg-neutral text-neutral-content text-sm font-bold">
                  <span className="truncate first-letter:uppercase">
                    {dayLabel(day)}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {dayCountLabel(products)}
                  </span>
                </h2>
                <div className="divide-y divide-base-300">
                  {products.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      onOpen={() => setOpenId(product.id)}
                    />
                  ))}
                </div>
              </section>
            ))}
            {/* La liste s'arrête où s'arrête la rétention ; l'historique, lui,
                continue. Sans le lien : à soixante-dix lignes, la fin de liste
                est à huit écrans d'ici. */}
            <p className="px-4 py-6 text-center text-xs text-base-content/70">
              L'appareil garde sept jours. Au-delà, c'est dans OneDrive.
            </p>
          </>
        )}
      </div>

      <div className="p-4 border-t border-base-300 bg-base-100 flex flex-col gap-2">
        <button
          type="button"
          onClick={() =>
            folderUrl && window.open(folderUrl, "_blank", "noopener,noreferrer")
          }
          disabled={folderLoading || !folderUrl}
          className="btn btn-ghost btn-block font-normal underline text-base-content/80"
        >
          {folderLoading && (
            <span className="loading loading-spinner loading-xs"></span>
          )}
          Voir tout l'historique dans OneDrive ↗
        </button>
        <button
          className="btn btn-primary btn-block btn-lg"
          onClick={() => onStart()}
        >
          Démarrer
        </button>
      </div>
    </div>
  );
};

export default ProductList;
